"use client";

import React, { useState } from "react";
import { PasswordInput } from "./PasswordInput";

export interface ResetPasswordPageProps {
  /** Portal title displayed below the logo */
  title?: string;
  /** Description text below the title */
  description?: string;
  /** Theme color for accents */
  theme?: "teal" | "violet" | "blue" | "green" | "amber" | "rose";
  /** Logo component or image */
  logo?: React.ReactNode | null;
  /** Logo URL if not providing a custom logo component */
  logoUrl?: string;
  /** Logo alt text */
  logoAlt?: string;
  /** Called when form is submitted with new password */
  onSubmit: (password: string) => Promise<void>;
  /** Login link URL after successful reset */
  loginUrl?: string;
  /** Support contact info */
  supportEmail?: string;
  /** Minimum password length */
  minPasswordLength?: number;
  /** Show trust indicators at bottom */
  showTrustIndicators?: boolean;
  /** Error from URL params (e.g., expired link) */
  initialError?: string | null;
}

const themeConfig = {
  teal: {
    gradient: "from-[#f0f9ff] via-white to-[#fef5ee]",
    decorative1: "bg-primary-200",
    decorative2: "bg-secondary-100",
    button: "bg-primary-500 hover:bg-primary-600 focus:ring-primary-500",
    buttonShadow: "shadow-primary-500/20 hover:shadow-primary-500/30",
    link: "text-primary-500 hover:text-primary-600",
    ring: "ring-primary-100",
    iconBg: "bg-primary-100",
    iconColor: "text-primary-600",
    checkColor: "text-primary-500",
  },
  violet: {
    gradient: "from-violet-50 via-white to-purple-50",
    decorative1: "bg-violet-200",
    decorative2: "bg-purple-200",
    button: "bg-violet-600 hover:bg-violet-700 focus:ring-violet-500",
    buttonShadow: "shadow-violet-500/20 hover:shadow-violet-500/30",
    link: "text-violet-600 hover:text-violet-500",
    ring: "ring-violet-100",
    iconBg: "bg-violet-100",
    iconColor: "text-violet-600",
    checkColor: "text-violet-500",
  },
  blue: {
    gradient: "from-blue-50 via-white to-indigo-50",
    decorative1: "bg-blue-200",
    decorative2: "bg-indigo-200",
    button: "bg-blue-600 hover:bg-blue-700 focus:ring-blue-500",
    buttonShadow: "shadow-blue-500/20 hover:shadow-blue-500/30",
    link: "text-blue-600 hover:text-blue-500",
    ring: "ring-blue-100",
    iconBg: "bg-blue-100",
    iconColor: "text-blue-600",
    checkColor: "text-blue-500",
  },
  green: {
    gradient: "from-green-50 via-white to-emerald-50",
    decorative1: "bg-green-200",
    decorative2: "bg-emerald-200",
    button: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
    buttonShadow: "shadow-green-500/20 hover:shadow-green-500/30",
    link: "text-green-600 hover:text-green-500",
    ring: "ring-green-100",
    iconBg: "bg-green-100",
    iconColor: "text-green-600",
    checkColor: "text-green-500",
  },
  amber: {
    gradient: "from-amber-50 via-white to-orange-50",
    decorative1: "bg-amber-200",
    decorative2: "bg-orange-200",
    button: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
    buttonShadow: "shadow-amber-500/20 hover:shadow-amber-500/30",
    link: "text-amber-600 hover:text-amber-500",
    ring: "ring-amber-100",
    iconBg: "bg-amber-100",
    iconColor: "text-amber-600",
    checkColor: "text-amber-500",
  },
  rose: {
    gradient: "from-rose-50 via-white to-pink-50",
    decorative1: "bg-rose-200",
    decorative2: "bg-pink-200",
    button: "bg-rose-600 hover:bg-rose-700 focus:ring-rose-500",
    buttonShadow: "shadow-rose-500/20 hover:shadow-rose-500/30",
    link: "text-rose-600 hover:text-rose-500",
    ring: "ring-rose-100",
    iconBg: "bg-rose-100",
    iconColor: "text-rose-600",
    checkColor: "text-rose-500",
  },
};

interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
}

export function ResetPasswordPage({
  title = "Create new password",
  description = "Your new password must be different from previously used passwords.",
  theme = "teal",
  logo,
  logoUrl = "https://cdn.prod.website-files.com/65154e68ae1ad53c6e8aaa1a/6516a44d8f77816b807f929d_Logo.svg",
  logoAlt = "Alli",
  onSubmit,
  loginUrl = "/login",
  supportEmail,
  minPasswordLength = 8,
  showTrustIndicators = true,
  initialError = null,
}: ResetPasswordPageProps) {
  const colors = themeConfig[theme];

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(initialError);
  const [isSuccess, setIsSuccess] = useState(false);

  const passwordRequirements: PasswordRequirement[] = [
    { label: `At least ${minPasswordLength} characters`, test: (p) => p.length >= minPasswordLength },
    { label: "Contains uppercase letter", test: (p) => /[A-Z]/.test(p) },
    { label: "Contains lowercase letter", test: (p) => /[a-z]/.test(p) },
    { label: "Contains number", test: (p) => /[0-9]/.test(p) },
  ];

  const allRequirementsMet = passwordRequirements.every((req) => req.test(password));
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!allRequirementsMet) {
      setError("Please meet all password requirements.");
      return;
    }

    if (!passwordsMatch) {
      setError("Passwords do not match.");
      return;
    }

    setIsLoading(true);

    try {
      await onSubmit(password);
      setIsSuccess(true);
    } catch (err) {
      if (err instanceof Error) {
        setError(err.message);
      } else {
        setError("An unexpected error occurred. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={`min-h-screen flex flex-col bg-gradient-to-br ${colors.gradient}`}>
      {/* Decorative background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute -top-40 -right-40 w-[500px] h-[500px] ${colors.decorative1} rounded-full blur-3xl opacity-20`} />
        <div className={`absolute -bottom-40 -left-40 w-[500px] h-[500px] ${colors.decorative2} rounded-full blur-3xl opacity-30`} />
        <div className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] ${colors.ring} rounded-full opacity-50`} />
      </div>

      {/* Main content */}
      <div className="flex-1 flex flex-col justify-center py-12 px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          {/* Logo */}
          <div className="flex justify-center">
            {logo || (
              <img
                src={logoUrl}
                alt={logoAlt}
                className="h-20 w-auto"
              />
            )}
          </div>

          {/* Title & Description */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-bold text-gray-900 tracking-tight">
              {title}
            </h1>
            <p className="mt-2 text-gray-600">
              {description}
            </p>
          </div>

          {/* Form Card */}
          <div className="bg-white/80 backdrop-blur-xl rounded-3xl shadow-xl shadow-gray-200/50 border border-white/60 p-8 sm:p-10">
            {isSuccess ? (
              /* Success State */
              <div className="text-center animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className={`mx-auto w-16 h-16 ${colors.iconBg} rounded-full flex items-center justify-center mb-6`}>
                  <svg className={`w-8 h-8 ${colors.iconColor}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h2 className="text-xl font-semibold text-gray-900 mb-2">Password reset successful</h2>
                <p className="text-gray-600 mb-6">
                  Your password has been successfully updated. You can now sign in with your new password.
                </p>
                <a
                  href={loginUrl}
                  className={`
                    inline-flex items-center justify-center
                    px-6 py-3 text-base font-semibold text-white
                    rounded-full shadow-lg ${colors.buttonShadow}
                    transition-all duration-200
                    ${colors.button}
                    focus:outline-none focus:ring-2 focus:ring-offset-2
                    transform hover:scale-[1.02] active:scale-[0.98]
                  `}
                >
                  Sign in
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14 5l7 7m0 0l-7 7m7-7H3" />
                  </svg>
                </a>
              </div>
            ) : (
              <>
                {/* Error Alert */}
                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-100 rounded-2xl flex items-start gap-3 animate-in fade-in slide-in-from-top-2 duration-300">
                    <div className="flex-shrink-0 w-5 h-5 rounded-full bg-red-100 flex items-center justify-center mt-0.5">
                      <svg className="w-3 h-3 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                <form className="space-y-5" onSubmit={handleSubmit}>
                  <PasswordInput
                    label="New password"
                    value={password}
                    onChange={setPassword}
                    placeholder="Enter new password"
                    id="password"
                    className="rounded-xl"
                  />

                  {/* Password requirements */}
                  {password.length > 0 && (
                    <div className="space-y-2 animate-in fade-in slide-in-from-top-2 duration-300">
                      {passwordRequirements.map((req, index) => (
                        <div key={index} className="flex items-center gap-2 text-sm">
                          {req.test(password) ? (
                            <svg className={`w-4 h-4 ${colors.checkColor}`} fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          ) : (
                            <svg className="w-4 h-4 text-gray-300" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm0-2a6 6 0 100-12 6 6 0 000 12z" clipRule="evenodd" />
                            </svg>
                          )}
                          <span className={req.test(password) ? "text-gray-700" : "text-gray-400"}>
                            {req.label}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  <PasswordInput
                    label="Confirm new password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                    placeholder="Confirm new password"
                    id="confirmPassword"
                    className="rounded-xl"
                  />

                  {/* Password match indicator */}
                  {confirmPassword.length > 0 && (
                    <div className="flex items-center gap-2 text-sm animate-in fade-in duration-200">
                      {passwordsMatch ? (
                        <>
                          <svg className={`w-4 h-4 ${colors.checkColor}`} fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Passwords match</span>
                        </>
                      ) : (
                        <>
                          <svg className="w-4 h-4 text-red-400" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
                          </svg>
                          <span className="text-red-500">Passwords do not match</span>
                        </>
                      )}
                    </div>
                  )}

                  <button
                    type="submit"
                    disabled={isLoading || !allRequirementsMet || !passwordsMatch}
                    className={`
                      w-full inline-flex items-center justify-center
                      px-6 py-3.5 text-base font-semibold text-white
                      rounded-full shadow-lg ${colors.buttonShadow}
                      transition-all duration-200
                      disabled:opacity-50 disabled:cursor-not-allowed
                      ${colors.button}
                      focus:outline-none focus:ring-2 focus:ring-offset-2
                      transform hover:scale-[1.02] active:scale-[0.98]
                    `}
                  >
                    {isLoading ? (
                      <span className="flex items-center justify-center">
                        <svg
                          className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                          xmlns="http://www.w3.org/2000/svg"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          />
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          />
                        </svg>
                        Updating password...
                      </span>
                    ) : (
                      <>
                        Reset password
                        <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </>
                    )}
                  </button>
                </form>

                {/* Support footer */}
                {supportEmail && (
                  <div className="mt-8 pt-6 border-t border-gray-100">
                    <p className="text-center text-sm text-gray-500">
                      Need help?{" "}
                      <a
                        href={`mailto:${supportEmail}`}
                        className={`font-medium ${colors.link} transition-colors`}
                      >
                        Contact support
                      </a>
                    </p>
                  </div>
                )}
              </>
            )}
          </div>

          {/* Trust indicators */}
          {showTrustIndicators && (
            <div className="mt-8 flex flex-col items-center gap-4">
              <div className="flex items-center gap-6 text-gray-400">
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  <span className="text-xs">256-bit SSL</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                  <span className="text-xs">PHIPA Compliant</span>
                </div>
                <div className="flex items-center gap-2">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                  </svg>
                  <span className="text-xs">Trusted Care</span>
                </div>
              </div>
              <p className="text-xs text-gray-400">
                Your privacy and security are our top priority
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
